"use client";

import { useBookingResume } from "~/hooks/use-booking-resume";
import { Suspense, useEffect, useState } from "react";
import { buttonVariants } from "~/components/ui/button";
import { Avatar, AvatarImage } from "~/components/ui/avatar";
import { ArrowRight, Calendar, CalendarPlus } from "lucide-react";
import Link from "next/link";

import { capturePostHogEvent } from "~/lib/posthog/client";
import { cn } from "~/lib/utils";

export function MobileStickyCtaBar(props: {
  href?: string;
  label?: string;
  trackHomepageCta?: boolean;
}) {
  return (
    <Suspense fallback={null}>
      <MobileStickyCtaContent {...props} />
    </Suspense>
  );
}

function MobileStickyCtaContent({
  href = "/slobodni-termini",
  label = "Zakaži prvi razgovor",
  trackHomepageCta = false,
}: {
  href?: string;
  label?: string;
  trackHomepageCta?: boolean;
}) {
  const { resumeUrl, cartItem } = useBookingResume();
  const resumeBooking = trackHomepageCta && resumeUrl;
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const sentinel = document.getElementById("hero-cta-inline");
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setShowSticky(!entry.isIntersecting);
        }
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  if (!showSticky) return null;

  return (
    <div className="fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 z-50 sm:hidden">
      <Link
        translate="no"
        className={cn(
          buttonVariants({ size: "lg", variant: "cta" }),
          "group h-14 w-full gap-3 rounded-2xl px-4 text-base font-semibold tracking-normal shadow-lg",
          resumeBooking && "justify-start",
        )}
        href={resumeBooking || href}
        aria-label={resumeBooking ? "Nastavi zakazivanje" : label}
        onClick={() => {
          if (trackHomepageCta) {
            capturePostHogEvent("homepage_cta_clicked", {
              placement: "mobile_sticky",
              destination: resumeBooking ? "booking_resume" : "availability",
            });
          }
        }}
      >
        {resumeBooking && cartItem?.therapistImage && (
          <Avatar className="h-8 w-8" aria-hidden="true">
            <AvatarImage src={cartItem.therapistImage} alt="" />
          </Avatar>
        )}
        {resumeBooking ? "Nastavi zakazivanje" : label}
        {resumeBooking && (
          <ArrowRight className="ml-auto h-5 w-5 shrink-0" aria-hidden="true" />
        )}
        {!resumeBooking && (
          <>
            <Calendar className="ml-3 block h-4 w-4 group-hover:hidden" />
            <CalendarPlus className="ml-3 hidden h-4 w-4 group-hover:block" />
          </>
        )}
      </Link>
    </div>
  );
}
